import { useState } from "react";
import { Card } from "../Card";
import { Button } from "../Button";
import { useQuery } from "@tanstack/react-query";
import { ShowToast } from "../../services/toast";
import { EntityTable } from "./EntityTable";
import { EntityFormModal } from "./EntityFormModal";
import { EntityConfirmModal } from "./EntityConfirmModal";
import { EntityItem, EntityApi, EntityLabels, getEntityLabels } from "./types";

export type EntityListConfig = {
  queryKey: string;
  api: EntityApi;
  labels: EntityLabels;
};

export function EntityList({ config }: { config: EntityListConfig }) {
  const { queryKey, api, labels } = config;
  const { manageTitle, addButton, deletedSuccess, deleteFailed } = getEntityLabels(labels);

  const [editItem, setEditItem] = useState<EntityItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<EntityItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: [queryKey],
    queryFn: () => api.getList(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const items: EntityItem[] = data?.data ?? [];

  const handleAdd = () => {
    setEditItem(null);
    setIsFormOpen(true);
  };

  const handleDelete = async (item: EntityItem) => {
    try {
      setDeleteLoading(true);
      const res = await api.delete(item.id);
      if (res?.status === 200) {
        ShowToast({ message: deletedSuccess, type: "success" });
        await refetch();
        setDeleteItem(null);
      } else {
        ShowToast({ message: deleteFailed, type: "error" });
        setDeleteItem(null);
      }
    } catch {
      ShowToast({ message: deleteFailed, type: "error" });
      setDeleteItem(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = (item: EntityItem) => {
    setEditItem(item);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditItem(null);
  };

  return (
    <>
      <Card>
        <div className="md:mb-6 flex items-center justify-between">
          <div>
            <div className="text-text-primary text-2xl font-bold">{manageTitle}</div>
          </div>
          <Button variant="primary" className="!py-3 !rounded-full" onClick={handleAdd}>
            {addButton}
          </Button>
        </div>
        <EntityTable
          data={items}
          isLoading={isLoading || isRefetching}
          onEdit={handleEdit}
          onDelete={setDeleteItem}
        />
      </Card>
      <EntityFormModal
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        item={editItem}
        onSuccess={() => {
          handleCloseForm();
          refetch();
        }}
        api={api}
        labels={labels}
      />
      <EntityConfirmModal
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={() => deleteItem && handleDelete(deleteItem)}
        loading={deleteLoading}
        item={deleteItem}
        labels={labels}
      />
    </>
  );
}
